<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: '100%' }"
    title="Send welcome email to users"
  >
    <div class="card q-pa-md no-wrap full-height scroll">
      <div class="q-pa-md q-gutter-md">
        <QTable
          v-model:pagination="pagination"
          class="full-width"
          :columns="columns"
          :loading="loading"
          row-key="name"
          :rows="mappedUsers"
          :rows-per-page-options="rowPerPage"
          title="Users to Welcome"
          wrap-cells
          @request="onRequest"
        >
          <template #top>
            <QRadio
              v-model="selector"
              label="Select All"
              val="all"
            />

            <QRadio
              v-model="selector"
              label="Select New Users"
              val="new"
            />

            <QRadio
              v-model="selector"
              label="Select users who have never signed in"
              val="inactive"
            />
          </template>

          <template #top-row>
            <QTr class="filter-row">
              <QTd />
              <QTd>
                <QInput
                  v-model="filters.details"
                  borderless
                  clearable
                  :debounce="300"
                  dense
                  placeholder="Search"
                  square
                />
              </QTd>
              <QTd />
              <QTd />
              <QTd />
              <QTd />
              <QTd />
            </QTr>
          </template>

          <template #body="props">
            <QTr :props="props">
              <QTd
                key="selected"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <QCheckbox v-model="props.row.selected" />
              </QTd>
              <QTd
                key="name"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                {{ props.row.name }}
              </QTd>
              <QTd
                key="team"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                {{ props.row.primaryOrganisationTeamName("") }}
              </QTd>
              <QTd
                key="createdAt"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <span v-if="props.row.createdAt">
                  {{
                    formatIntlDate(props.row.createdAt, {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: getTimezone().timeZone,
                    })
                  }}
                </span>
              </QTd>
              <QTd
                key="currentSignInAt"
                class="vertical-top"
                :props="props"
              >
                <span v-if="props.row.currentSignInAt">
                  {{
                    formatIntlDate(props.row.currentSignInAt, {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: getTimezone().timeZone,
                    })
                  }}
                </span>
                <span v-else>Never</span>
              </QTd>
              <QTd
                key="welcomeSentAt"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <span v-if="props.row.welcomeSentAt">
                  {{
                    formatIntlDate(props.row.welcomeSentAt, {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: getTimezone().timeZone,
                    })
                  }}
                </span>
                <span v-else>Never</span>
              </QTd>
              <QTd
                key="welcomeEmailOption"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <SelectList
                  v-model="props.row.welcome_email_option"
                  class="q-ma-none q-pa-none"
                  dense
                  emit-value
                  label="Welcome email option"
                  :options="emailContactOptions(props.row)"
                />
              </QTd>
            </QTr>
          </template>
        </QTable>
        <QCheckbox
          v-model="emailAccountManagers"
          label="Also send the email to account managers"
        />
        <div>
          <p>
            Sending a welcome email, resets the pin but DOES NOT unlock the
            account after failing to log in too many times.
          </p>
          <p>
            To unlock the account use the
            <strong> Send new Temporary PIN via email </strong>
            button on the Organisation's users page.
          </p>
        </div>
      </div>
    </div>

    <template #buttons>
      <BaseButton
        color="primary"
        :disabled="formDisabled || loading"
        :label="welcomeLabel"
        new-design
        @click="confirm"
      />
    </template>
  </Modal>
</template>

<script>
import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { EMAIL_CONTACT_OPTIONS } from "shared/constants";
import { formatIntlDate, getTimezone } from "shared/helpers/date";
import { pluralize } from "shared/helpers/string";
import { User } from "shared/resources";

import SelectList from "src/components/common/SelectList.vue";
import UserService from "src/services/api/UserService";

export default {
  name: "WelcomeUsersModal",
  components: {
    BaseButton,
    SelectList,
    Modal,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ["confirm"],
  data() {
    return {
      emailAccountManagers: true,
      selector: "",
      mappedUsers: [],
      columns: [
        {
          name: "selected",
          required: true,
          label: "Select",
          align: "left",
          field: "selected",
          sortable: true,
          style: "width: 5%",
          classes: "wrap-table-content",
        },
        {
          name: "name",
          required: true,
          label: "Name",
          align: "left",
          field: "name",
          sortable: true,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "team",
          required: true,
          label: "Team",
          align: "left",
          field: "team",
          sortable: false,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "createdAt",
          required: true,
          label: "Created at",
          align: "left",
          field: "createdAt",
          sortable: true,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "currentSignInAt",
          required: true,
          label: "Last sign in at",
          align: "left",
          field: "currentSignInAt",
          sortable: true,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "welcomeSentAt",
          required: true,
          label: "Welcomed at",
          align: "left",
          field: "welcomeSentAt",
          sortable: true,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "welcomeEmailOption",
          required: true,
          label: "Include Contact Info",
          align: "center",
          field: "welcome_email_option",
          sortable: false,
          style: "width: 25%",
          classes: "wrap-table-content",
        },
      ],
      rowPerPage: [8, 25, 50, 100],
      pagination: {
        page: 1,
        rowsPerPage: 8,
        rowsNumber: 0,
        sortBy: "createdAt",
        descending: true,
      },
      loading: false,
      filters: {
        details: null,
      },
    };
  },
  computed: {
    usersToWelcome() {
      return this.mappedUsers.filter((user) => user.selected);
    },
    formDisabled() {
      return !this.organisationId || !this.usersToWelcome.length;
    },
    welcomeLabel() {
      const pluralString = pluralize(this.usersToWelcome.length, "User");

      return `Welcome ${this.usersToWelcome.length} ${pluralString}`;
    },
    comparator() {
      switch (this.selector) {
        case "new":
          return (user) => !user.welcomeSentAt && !user.currentSignInAt;
        case "inactive":
          return (user) => !user.currentSignInAt;
        default:
          return (user) => Boolean(user);
      }
    },
  },
  watch: {
    selector() {
      this.mappedUsers.forEach((user) =>
        Object.assign(user, {
          selected: this.comparator(user),
        })
      );
    },
    filters: {
      deep: true,
      handler() {
        this.loadAndPrepareUsers();
      },
    },
  },
  mounted() {
    this.loadAndPrepareUsers();
  },
  methods: {
    formatIntlDate,
    getTimezone,
    emailContactOptions(user) {
      return EMAIL_CONTACT_OPTIONS.filter((option) =>
        option.region.includes(user.organisation.region)
      );
    },
    async loadAndPrepareUsers() {
      try {
        this.loading = true;

        const filters = { active: true, organisationId: this.organisationId };

        Object.keys(this.filters).forEach((filter) => {
          if (["details"].some((key) => filter.includes(key))) {
            filters[filter] = { match: this.filters[filter] };
          } else {
            filters[filter] = this.filters[filter];
          }
        });

        const response = await User.includes([
          "primaryOrganisationTeam",
          "organisation",
        ])
          .where(filters)
          .order({
            createdAt: "desc",
          })
          .select({
            organisations: ["id", "welcomeEmailOption", "region"],
            users: [
              "createdAt",
              "name",
              "welcomeSentAt",
              "timeZone",
              "currentSignInAt",
            ],
            organisationTeams: ["name"],
          })
          .stats({ total: "count" })
          .page(this.pagination.page)
          .per(this.pagination.rowsPerPage)
          .all();

        this.pagination.rowsNumber = response.meta.stats.total.count;

        const users = response.data;

        users.forEach((user) =>
          Object.assign(user, {
            selected: false,
            welcome_email_option: user.organisation.welcomeEmailOption,
          })
        );

        this.mappedUsers = users;
      } catch (error) {
        this.$error("Something went wrong loading users", error);
      } finally {
        this.loading = false;
      }
    },
    onRequest({ pagination }) {
      Object.assign(this.pagination, pagination);
      this.loadAndPrepareUsers();
    },
    welcomeUsers() {
      return Promise.all(
        this.usersToWelcome.map(({ id, welcome_email_option: option }) =>
          UserService.sendWelcome(id, this.emailAccountManagers, option)
        )
      );
    },
    async confirm() {
      this.loading = true;

      try {
        await this.welcomeUsers();

        this.$toast(
          `Successfully welcomed ${this.usersToWelcome.length} users`
        );

        this.$emit("confirm");
      } catch (error) {
        this.$error("Error welcoming users", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  .wrap-table-content {
    font-size: 14px;
    white-space: initial;
  }

  .nowrap-table-content {
    font-size: 14px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

table tbody tr td a {
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    color: #f95858;
  }
}

:deep(.streem-modal) {
  .container {
    max-width: 1500px;
  }
}
</style>
